import {
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { TipButton } from "Components/Shared/TipSelector/TipButton";
import { Resources, useResource } from "Translations/Resources";
import { getCurrencySymbol } from "Utils/CurrencyUtils";
import type { TipMode } from "Components/Shared/TipSelector/types";

const RESOURCES = Resources.Customer.Pay;
const MAX_CUSTOM_TIP = 1_000_000;
const BUTTON_MODES: ReadonlyArray<Exclude<TipMode, "none">> = [
  "fivePercent",
  "tenPercent",
  "twentyPercent",
  "custom",
];

type Props = {
  tipMode: TipMode;
  currencyCode?: string;
  customTipAmount?: number;
  onTipModeChange: (mode: TipMode) => void;
  onCustomTipAmountChange: (amount: number | undefined) => void;
};

export const TipSelector = ({
  tipMode,
  customTipAmount,
  currencyCode,
  onTipModeChange,
  onCustomTipAmountChange,
}: Props) => {
  const { t } = useResource();
  const currencySymbol = currencyCode ? getCurrencySymbol(currencyCode) : "";

  const handleTipOptionClick = (mode: TipMode) => {
    onTipModeChange(mode);
    if (mode !== "custom") {
      onCustomTipAmountChange(undefined);
    }
  };

  const handleNoTipToggle = (isChecked: boolean) => {
    if (isChecked) {
      onTipModeChange("none");
      onCustomTipAmountChange(undefined);
    }
  };

  const handleCustomAmountChange = (value: string) => {
    if (value === "") {
      onCustomTipAmountChange(undefined);
      return;
    }
    const parsed = Number(value.replace(",", "."));
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= MAX_CUSTOM_TIP) {
      onCustomTipAmountChange(parsed);
    }
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1}>
        {BUTTON_MODES.map((mode) => (
          <TipButton
            key={mode}
            mode={mode}
            label={t(RESOURCES[mode])}
            isSelected={tipMode === mode}
            onClick={handleTipOptionClick}
          />
        ))}
      </Stack>
      {tipMode === "custom" && (
        <Box>
          <TextInput
            autoFocus
            inputMode="decimal"
            label={t(RESOURCES.TipCustomAmountLabel)}
            value={customTipAmount?.toString() ?? ""}
            mask={Number}
            scale={2}
            onMaskInputChange={handleCustomAmountChange}
            inputEndAdornment={
              <InputAdornment position="end">{currencySymbol}</InputAdornment>
            }
            min={0}
            disableHelperText
            sx={{
              "& .MuiOutlinedInput-root": { backgroundColor: "common.white" },
            }}
          />
        </Box>
      )}
      <FormControlLabel
        sx={{ ml: 0, gap: 1 }}
        control={
          <Checkbox
            size="small"
            checked={tipMode === "none"}
            onChange={(e) => handleNoTipToggle(e.target.checked)}
            sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 20 } }}
          />
        }
        label={
          <Typography
            fontSize={12}
            fontWeight={400}
            lineHeight="20px"
            color="common.black"
          >
            {t(RESOURCES.TipContinueWithout)}
          </Typography>
        }
      />
    </Stack>
  );
};
